import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { formatYmdLocal, lastNDaysRange } from '../../utils/trainingHistoryRange';
import {
  BRAND_CHART_CSS_PRIMARY,
  resolveBrandChartColors,
} from '../../utils/brandChartColors';
import {
  formatPaceLabel,
  isSportSessionKind,
  normalizeSessionKind,
  SESSION_KINDS,
} from '../../utils/trainingSportSession';

function parseMetricNumbers(raw, { allowZero = false } = {}) {
  let parts = [];
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    parts = Object.values(raw);
  } else if (Array.isArray(raw)) {
    parts = raw;
  } else if (raw != null && String(raw).trim() !== '') {
    parts = String(raw).split(/[/|,;]+/);
  }
  return parts
    .map((v) => Number(String(v).trim().replace(',', '.')))
    .filter((n) => Number.isFinite(n) && (allowZero ? n >= 0 : n > 0));
}

function avgNums(vals) {
  if (!vals?.length) return null;
  return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10;
}

function dayLabel(fecha) {
  try {
    const d = new Date(`${fecha}T12:00:00`);
    return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
  } catch {
    return String(fecha).slice(5);
  }
}

function resolveStrokeColors() {
  return resolveBrandChartColors();
}

function exerciseNameOf(ex) {
  return String(ex?.exercise_name || ex?.nombre || '').trim();
}

function listGymExercises(logs, plan) {
  const names = new Set();
  for (const log of logs || []) {
    for (const ex of log?.ejercicios || []) {
      const n = exerciseNameOf(ex);
      if (n) names.add(n);
    }
  }
  for (const day of plan?.dias || plan?.days || []) {
    if (normalizeSessionKind(day?.session_kind) !== SESSION_KINDS.GYM) continue;
    for (const ex of day?.ejercicios || []) {
      const n = exerciseNameOf(ex);
      if (n) names.add(n);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b, 'es'));
}

function prescriptionForLog(log, plan) {
  const days = plan?.dias || plan?.days || [];
  if (!Array.isArray(days) || !days.length) return null;
  const dia = Number(log?.dia);
  const byNumero = days.find((d) => Number(d?.dia) === dia);
  if (byNumero?.sport_prescription) return byNumero.sport_prescription;
  const idx = Number.isFinite(dia) && dia >= 1 ? dia - 1 : 0;
  return days[idx]?.sport_prescription || null;
}

function sessionLoadReps(log, exerciseFilter = '') {
  const weights = [];
  const reps = [];
  const want = String(exerciseFilter || '').trim().toLowerCase();
  for (const ex of log?.ejercicios || []) {
    if (want && exerciseNameOf(ex).toLowerCase() !== want) continue;
    const setWeights = parseMetricNumbers(ex?.set_weights || ex?.setWeights);
    const w = setWeights.length
      ? setWeights
      : [
        ...parseMetricNumbers(ex?.weights_done),
        ...(Number(ex?.weight_kg) > 0 ? [Number(ex.weight_kg)] : []),
      ];
    const setReps = parseMetricNumbers(ex?.set_reps || ex?.setReps);
    const r = setReps.length ? setReps : parseMetricNumbers(ex?.reps_done);
    weights.push(...w);
    reps.push(...r);
  }
  return { carga: avgNums(weights), reps: avgNums(reps) };
}

function buildGymSeries(logs, measurements, exerciseFilter = '') {
  const byFecha = new Map();
  for (const log of logs || []) {
    const fecha = String(log?.fecha || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) continue;
    const { carga, reps } = sessionLoadReps(log, exerciseFilter);
    const prev = byFecha.get(fecha) || { fecha, _cargas: [], _reps: [] };
    if (carga != null) prev._cargas.push(carga);
    if (reps != null) prev._reps.push(reps);
    byFecha.set(fecha, prev);
  }
  for (const m of measurements || []) {
    const fecha = String(m?.recorded_at || m?.fecha || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) continue;
    const peso = Number(m?.weight_kg);
    if (!Number.isFinite(peso) || peso <= 0) continue;
    const prev = byFecha.get(fecha) || { fecha, _cargas: [], _reps: [] };
    prev.peso = peso;
    byFecha.set(fecha, prev);
  }
  return [...byFecha.values()]
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((row) => ({
      fecha: row.fecha,
      label: dayLabel(row.fecha),
      carga: avgNums(row._cargas),
      reps: avgNums(row._reps),
      peso: row.peso != null ? Number(row.peso) : null,
    }))
    .filter((r) => r.carga != null || r.reps != null || r.peso != null);
}

function buildRunningSeries(logs, plan) {
  const byFecha = new Map();
  for (const log of logs || []) {
    const fecha = String(log?.fecha || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) continue;
    const sl = log.sport_log || {};
    const pres = prescriptionForLog(log, plan) || {};
    const distKm = Number(sl.distance_m) > 0
      ? Math.round((Number(sl.distance_m) / 1000) * 100) / 100
      : (Number(sl.distance_km) > 0 ? Number(sl.distance_km) : null);
    const paceMin = Number(sl.pace_sec_per_km) > 0
      ? Math.round((Number(sl.pace_sec_per_km) / 60) * 100) / 100
      : null;
    const ritmoMin = Number(pres.pace_target_sec_per_km) > 0
      ? Math.round((Number(pres.pace_target_sec_per_km) / 60) * 100) / 100
      : null;
    const durMin = Number(sl.duration_sec) > 0
      ? Math.round((Number(sl.duration_sec) / 60) * 10) / 10
      : null;
    const hr = Number(sl.hr_avg_ppm) > 0 ? Number(sl.hr_avg_ppm) : null;
    if (distKm == null && paceMin == null && ritmoMin == null && durMin == null && hr == null) continue;
    const prev = byFecha.get(fecha) || {
      fecha,
      _dist: [],
      _pace: [],
      _ritmo: [],
      _dur: [],
      _hr: [],
    };
    if (distKm != null) prev._dist.push(distKm);
    if (paceMin != null) prev._pace.push(paceMin);
    if (ritmoMin != null) prev._ritmo.push(ritmoMin);
    if (durMin != null) prev._dur.push(durMin);
    if (hr != null) prev._hr.push(hr);
    byFecha.set(fecha, prev);
  }
  return [...byFecha.values()]
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((row) => ({
      fecha: row.fecha,
      label: dayLabel(row.fecha),
      distancia: avgNums(row._dist),
      pace: avgNums(row._pace),
      ritmo: avgNums(row._ritmo),
      tiempo: avgNums(row._dur),
      fc: avgNums(row._hr),
    }));
}

function buildSwimSeries(logs, plan) {
  const byFecha = new Map();
  for (const log of logs || []) {
    const fecha = String(log?.fecha || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) continue;
    const sl = log.sport_log || {};
    const pres = prescriptionForLog(log, plan) || {};
    const distM = Number(sl.distance_m) > 0 ? Number(sl.distance_m) : null;
    const ritmo = Number(sl.pace_per_100_sec) > 0 ? Number(sl.pace_per_100_sec) : null;
    const objetivo = Number(pres.pace_per_100_sec) > 0 ? Number(pres.pace_per_100_sec) : null;
    const durMin = Number(sl.duration_sec) > 0
      ? Math.round((Number(sl.duration_sec) / 60) * 10) / 10
      : null;
    const hr = Number(sl.hr_avg_ppm) > 0 ? Number(sl.hr_avg_ppm) : null;
    if (distM == null && ritmo == null && objetivo == null && durMin == null && hr == null) continue;
    const prev = byFecha.get(fecha) || {
      fecha,
      _dist: [],
      _ritmo: [],
      _objetivo: [],
      _dur: [],
      _hr: [],
    };
    if (distM != null) prev._dist.push(distM);
    if (ritmo != null) prev._ritmo.push(ritmo);
    if (objetivo != null) prev._objetivo.push(objetivo);
    if (durMin != null) prev._dur.push(durMin);
    if (hr != null) prev._hr.push(hr);
    byFecha.set(fecha, prev);
  }
  return [...byFecha.values()]
    .sort((a, b) => a.fecha.localeCompare(b.fecha))
    .map((row) => ({
      fecha: row.fecha,
      label: dayLabel(row.fecha),
      distancia: avgNums(row._dist),
      ritmo: avgNums(row._ritmo),
      objetivo: avgNums(row._objetivo),
      tiempo: avgNums(row._dur),
      fc: avgNums(row._hr),
    }));
}

function planHasKind(plan, kind) {
  const days = plan?.dias || plan?.days || [];
  if (!Array.isArray(days)) return false;
  return days.some((d) => normalizeSessionKind(d?.session_kind) === kind);
}

const MODALITIES = {
  gym: {
    id: 'gym',
    title: 'Gimnasio',
    subtitle: 'Peso corporal, carga y repeticiones',
    empty: 'Cuando registres series en el diario o medidas, verás aquí tu progreso de gimnasio.',
    series: [
      { id: 'peso', label: 'Peso', unit: 'kg', axis: 'left', type: 'line' },
      { id: 'carga', label: 'Carga', unit: 'kg', axis: 'left', type: 'area' },
      { id: 'reps', label: 'Reps', unit: '', axis: 'right', type: 'line', dash: true },
    ],
  },
  running: {
    id: 'running',
    title: 'Running',
    subtitle: 'Distancia, pace y ritmo objetivo del coach',
    empty: 'Cuando registres una sesión Running, verás aquí tu evolución.',
    series: [
      { id: 'distancia', label: 'Distancia', unit: 'km', axis: 'left', type: 'area' },
      { id: 'pace', label: 'Pace', unit: 'min/km', axis: 'right', type: 'line', dash: true },
      { id: 'ritmo', label: 'Ritmo', unit: 'min/km', axis: 'right', type: 'line' },
      { id: 'tiempo', label: 'Tiempo', unit: 'min', axis: 'left', type: 'line' },
      { id: 'fc', label: 'FC', unit: 'ppm', axis: 'right', type: 'line' },
    ],
  },
  swimming: {
    id: 'swimming',
    title: 'Natación',
    subtitle: 'Ritmo medio vs objetivo /100 m',
    empty: 'Cuando registres una sesión de natación, verás aquí tu evolución.',
    series: [
      { id: 'distancia', label: 'Distancia', unit: 'm', axis: 'left', type: 'area' },
      { id: 'ritmo', label: 'Ritmo medio', unit: 's/100m', axis: 'right', type: 'line', dash: true },
      { id: 'objetivo', label: 'Objetivo', unit: 's/100m', axis: 'right', type: 'line' },
      { id: 'tiempo', label: 'Tiempo', unit: 'min', axis: 'left', type: 'line' },
      { id: 'fc', label: 'FC', unit: 'ppm', axis: 'right', type: 'line' },
    ],
  },
};

function ModalityDashboardCard({
  modality,
  rows,
  colors,
  extraFilter = null,
}) {
  const cfg = MODALITIES[modality];
  const defaults = Object.fromEntries(cfg.series.map((s) => [s.id, true]));
  const [visible, setVisible] = useState(defaults);

  const hasAny = useMemo(
    () => rows.some((r) => cfg.series.some((s) => visible[s.id] && r[s.id] != null)),
    [rows, visible, cfg.series],
  );

  const colorFor = (idx) => {
    const map = [colors.b, colors.a, colors.c, colors.a];
    return map[idx % map.length];
  };

  const formatTip = (value, name) => {
    if (value == null) return ['—', name];
    if (name === 'Pace' || (name === 'Ritmo' && modality === 'running')) {
      return [formatPaceLabel(Number(value) * 60), name];
    }
    if (name === 'Ritmo medio' || name === 'Objetivo' || name === 'Ritmo') {
      return [`${value} s/100m`, name];
    }
    if (name === 'Distancia' && modality === 'running') return [`${value} km`, name];
    if (name === 'Distancia' && modality === 'swimming') return [`${value} m`, name];
    if (name === 'Tiempo') return [`${value} min`, name];
    if (name === 'FC') return [`${value} ppm`, name];
    if (name === 'Reps') return [value, name];
    return [`${value} kg`, name];
  };

  return (
    <div className="card p-4 sm:p-5 space-y-3 overflow-hidden">
      <div>
        <h3 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide m-0">
          {cfg.title}
        </h3>
        <p className="text-xs d28d-text-muted m-0 mt-1">{cfg.subtitle}</p>
      </div>

      {extraFilter}

      <div className="flex flex-wrap gap-1.5">
        {cfg.series.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            className={`text-xs px-2.5 py-1 rounded-full border transition-opacity ${
              visible[s.id]
                ? 'opacity-100 border-[var(--shell-primary,var(--d28d-yellow,#ffd700))] bg-[color-mix(in_srgb,var(--shell-primary,var(--d28d-yellow,#ffd700))_18%,transparent)] text-[var(--d28d-text)]'
                : 'opacity-50 border-white/15 text-[var(--d28d-text)]'
            }`}
            onClick={() => setVisible((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
          >
            <span
              className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
              style={{ background: colorFor(idx) }}
            />
            {s.label}
          </button>
        ))}
      </div>

      {!hasAny ? (
        <p className="text-sm d28d-text-muted m-0 border border-dashed border-white/15 rounded-xl p-6 text-center">
          {cfg.empty}
        </p>
      ) : (
        <div className="h-56 sm:h-64 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id={`athleteFill-${modality}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.b} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={colors.b} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                width={32}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--d28d-surface, #18181b)',
                  border: `1px solid color-mix(in srgb, ${BRAND_CHART_CSS_PRIMARY} 35%, transparent)`,
                  borderRadius: 12,
                  color: 'var(--d28d-text, #fafafa)',
                  fontSize: 12,
                }}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload;
                  return row?.fecha ? formatYmdLocal(new Date(`${row.fecha}T12:00:00`)) : '';
                }}
                formatter={formatTip}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(value) => (
                  <span className="text-[var(--d28d-text)] opacity-80">{value}</span>
                )}
              />
              {cfg.series.map((s, idx) => {
                if (!visible[s.id]) return null;
                const stroke = colorFor(idx);
                const yAxisId = s.axis === 'right' ? 'right' : 'left';
                if (s.type === 'area') {
                  return (
                    <Area
                      key={s.id}
                      yAxisId={yAxisId}
                      type="monotone"
                      dataKey={s.id}
                      name={s.label}
                      stroke={stroke}
                      fill={`url(#athleteFill-${modality})`}
                      strokeWidth={2.5}
                      dot={{ r: 3, strokeWidth: 0, fill: stroke }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  );
                }
                return (
                  <Line
                    key={s.id}
                    yAxisId={yAxisId}
                    type="monotone"
                    dataKey={s.id}
                    name={s.label}
                    stroke={stroke}
                    strokeWidth={2}
                    strokeDasharray={s.dash ? '4 3' : undefined}
                    dot={{ r: 3, strokeWidth: 0, fill: stroke }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/** Dashboards de evolución por plantilla: Gimnasio / Running / Natación. */
export default function AthleteTrainingEvolutionChart() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [plan, setPlan] = useState(null);
  const [gymExercise, setGymExercise] = useState('');
  const [planKinds, setPlanKinds] = useState({
    gym: true,
    running: false,
    swimming: false,
  });
  const [colors, setColors] = useState(() => resolveBrandChartColors());

  useEffect(() => {
    setColors(resolveStrokeColors());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const range = days > 0 ? lastNDaysRange(days) : null;
      const logParams = range ? { from: range.from, to: range.to } : {};
      const [logsRes, measRes, planRes] = await Promise.all([
        api.get('/training/my-logs', { params: logParams }).catch(() => null),
        api.get('/training/measurements/me').catch(() => null),
        api.get('/training/my-current-plan').catch(() => null),
      ]);
      const nextLogs = Array.isArray(logsRes?.data?.data) ? logsRes.data.data : [];
      const nextMeas = Array.isArray(measRes?.data?.data)
        ? measRes.data.data
        : (Array.isArray(measRes?.data?.history) ? measRes.data.history : []);
      const plan = planRes?.data?.plan || planRes?.data?.data || null;
      const hasRunningPlan = planHasKind(plan, SESSION_KINDS.RUNNING);
      const hasSwimPlan = planHasKind(plan, SESSION_KINDS.SWIMMING);
      const hasGymPlan = planHasKind(plan, SESSION_KINDS.GYM)
        || (!hasRunningPlan && !hasSwimPlan);
      setLogs(nextLogs);
      setMeasurements(nextMeas);
      setPlan(plan);
      setPlanKinds({
        gym: hasGymPlan,
        running: hasRunningPlan,
        swimming: hasSwimPlan,
      });
    } catch {
      setLogs([]);
      setMeasurements([]);
      setPlan(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredByRange = useMemo(() => {
    if (days <= 0) return { logs, measurements };
    const range = lastNDaysRange(days);
    return {
      logs: (logs || []).filter((l) => {
        const f = String(l?.fecha || '').slice(0, 10);
        return f >= range.from && f <= range.to;
      }),
      measurements: (measurements || []).filter((m) => {
        const f = String(m?.recorded_at || m?.fecha || '').slice(0, 10);
        return f >= range.from && f <= range.to;
      }),
    };
  }, [logs, measurements, days]);

  const gymLogs = useMemo(
    () => filteredByRange.logs.filter((l) => !isSportSessionKind(l?.session_kind)),
    [filteredByRange.logs],
  );
  const runningLogs = useMemo(
    () => filteredByRange.logs.filter(
      (l) => normalizeSessionKind(l?.session_kind) === SESSION_KINDS.RUNNING,
    ),
    [filteredByRange.logs],
  );
  const swimLogs = useMemo(
    () => filteredByRange.logs.filter(
      (l) => normalizeSessionKind(l?.session_kind) === SESSION_KINDS.SWIMMING,
    ),
    [filteredByRange.logs],
  );

  const gymExercises = useMemo(() => listGymExercises(gymLogs, plan), [gymLogs, plan]);

  useEffect(() => {
    if (!gymExercise) return;
    if (!gymExercises.some((n) => n === gymExercise)) setGymExercise('');
  }, [gymExercises, gymExercise]);

  const gymRows = useMemo(
    () => buildGymSeries(gymLogs, filteredByRange.measurements, gymExercise),
    [gymLogs, filteredByRange.measurements, gymExercise],
  );
  const runningRows = useMemo(() => buildRunningSeries(runningLogs, plan), [runningLogs, plan]);
  const swimRows = useMemo(() => buildSwimSeries(swimLogs, plan), [swimLogs, plan]);

  const showGym = gymRows.length > 0 || planKinds.gym;
  const showRunning = runningRows.length > 0 || planKinds.running;
  const showSwim = swimRows.length > 0 || planKinds.swimming;

  // Si el plan no trae gym explícito pero tampoco deportes, igual mostrar gimnasio.
  const anyVisible = showGym || showRunning || showSwim;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide m-0">
            Mi evolución Training
          </h2>
          <p className="text-xs d28d-text-muted m-0 mt-1">
            Dashboards por plantilla: Gimnasio, Running y Natación
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: '30 días', value: 30 },
            { label: '90 días', value: 90 },
            { label: 'Todo', value: 0 },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              className={days === p.value ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => setDays(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card p-4 sm:p-5">
          <div className="h-52 animate-pulse rounded-xl bg-white/5" />
        </div>
      ) : !anyVisible ? (
        <div className="card p-4 sm:p-5">
          <p className="text-sm d28d-text-muted m-0 border border-dashed border-white/15 rounded-xl p-6 text-center">
            Cuando registres entrenos o medidas, verás aquí tus dashboards por plantilla.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {showGym ? (
            <ModalityDashboardCard
              modality="gym"
              rows={gymRows}
              colors={colors}
              extraFilter={(
                <label className="block text-xs d28d-text-muted">
                  Ejercicios
                  <select
                    className="input mt-1 block w-full max-w-sm text-sm"
                    value={gymExercise}
                    onChange={(e) => setGymExercise(e.target.value)}
                  >
                    <option value="">Todos los ejercicios</option>
                    {gymExercises.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </label>
              )}
            />
          ) : null}
          {showRunning ? (
            <ModalityDashboardCard modality="running" rows={runningRows} colors={colors} />
          ) : null}
          {showSwim ? (
            <ModalityDashboardCard modality="swimming" rows={swimRows} colors={colors} />
          ) : null}
        </div>
      )}
    </div>
  );
}
